import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { GitMerge, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { adminService } from "../../services/adminService";

const formatMergeDate = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

const MergeData = () => {
  const [isMerging, setIsMerging] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [lastMergeTime, setLastMergeTime] = useState<string | null>(null);

  useEffect(() => {
    adminService
      .getLastMerge()
      .then(data => {
        if (data.last_merged_at) {
          setLastMergeTime(formatMergeDate(data.last_merged_at));
        }
      })
      .catch(() => {});
  }, []);

  const handleMerge = async () => {
    setMergeError(null);
    try {
      setIsMerging(true);
      const response = await adminService.performDataMerge();
      if (response.success) {
        const formattedDate = formatMergeDate(new Date().toISOString());
        setLastMergeTime(formattedDate);
        toast.success(
          response.message ??
            "Data synchronization and merging completed successfully!"
        );
      } else {
        setMergeError(response.message ?? "Failed to merge data repository.");
      }
    } catch (error: any) {
      console.error("Merge error:", error);
      const msg =
        error?.response?.data?.message ??
        error?.message ??
        "An unexpected error occurred during data merge.";
      setMergeError(msg);
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <AdminLayout>
      <div className="animate-in fade-in space-y-4 duration-500 md:space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-[#654321] md:text-3xl">
              Data Management
            </h1>
          </div>
        </div>

        <Card className="overflow-hidden border-border/40 bg-gradient-to-br from-white to-stone-50 shadow-xl">
          <CardHeader className="border-b bg-card/50 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="rounded-xl bg-[#654321] p-3 text-white shadow-lg shadow-[#654321]/20">
                <GitMerge className="h-8 w-8" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-[#4d3219]">
                  Merge Data Repository
                </CardTitle>
                <CardDescription className="mt-1 text-base text-stone-500">
                  Click the button below to start the intelligent merging
                  algorithm.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center space-y-6 p-12">
            <div className="group relative">
              <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-[#654321] to-[#a67c52] opacity-25 blur transition duration-1000 group-hover:opacity-50 group-hover:duration-200" />
              <Button
                onClick={handleMerge}
                disabled={isMerging}
                size="lg"
                className="relative h-20 rounded-full bg-[#654321] px-12 text-xl font-bold text-white shadow-2xl transition-all duration-300 hover:scale-105 hover:bg-[#4d3219] disabled:opacity-80"
              >
                {isMerging ? (
                  <>
                    <Loader2 className="mr-3 h-8 w-8 animate-spin" />
                    Merging Records...
                  </>
                ) : (
                  <>
                    <GitMerge className="mr-3 h-8 w-8" />
                    Merge Data
                  </>
                )}
              </Button>
            </div>

            {mergeError && (
              <div className="w-full max-w-2xl rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <p className="mb-1 text-sm font-semibold text-red-700">
                      Merge Failed
                    </p>
                    <p className="whitespace-pre-wrap break-words text-sm text-red-600">
                      {mergeError}
                    </p>
                  </div>
                  <button
                    onClick={() => setMergeError(null)}
                    className="shrink-0 rounded p-0.5 text-red-400 hover:bg-red-100 hover:text-red-600"
                    aria-label="Dismiss error"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-4 pb-10 md:grid-cols-3">
          {[
            {
              label: "Last Merge",
              value: lastMergeTime ?? "Never",
              color: "text-stone-600",
            },
          ].map(stat => (
            <div
              key={stat.label}
              className="flex flex-col items-center justify-center rounded-xl border border-stone-100 bg-white p-4 shadow-sm"
            >
              <span className="mb-1 text-xs font-bold uppercase tracking-wider text-stone-400">
                {stat.label}
              </span>
              <span className={`text-lg font-bold ${stat.color}`}>
                {stat.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default MergeData;
